/**
 * Fetch past a page script that replaced `window.fetch` and broke it.
 *
 * Some partner apps open our links in an Android WebView that injects its own
 * `fetch` wrapper. Prod 2026-09-29 and 2026-10-21: two Weatherbug readers on a
 * /product/<id> link, both with `fetch` thrown by `ReferenceError: XHRInterface
 * is not defined` at `fetch (<anonymous>:239:29)` -- the host's wrapper calls a
 * native bridge the WebView never exposed. The listing request never left the
 * page, the retry threw the same way, and the reader was sent home.
 *
 * A network failure rejects with a TypeError from the browser's own fetch, and
 * asking again with the same fetch is the right answer to that. Anything else
 * thrown by a fetch that is not the browser's own is the wrapper's bug, so ask
 * once more with a clean fetch borrowed from a hidden same-origin iframe, which
 * the host's injected script does not reach.
 */

/** True when `fn` is a browser built-in rather than a script's replacement. */
export function isNativeFunction(fn: unknown): boolean {
  if (typeof fn !== 'function') return false;
  try {
    return /\{\s*\[native code\]\s*\}\s*$/.test(Function.prototype.toString.call(fn));
  } catch {
    return false;
  }
}

let borrowedFrame: HTMLIFrameElement | null = null;

/**
 * The browser's own fetch, taken from a hidden blank iframe. The frame stays in
 * the document: removing it tears down its realm and cancels any request made
 * through it. Null when there is no document or the frame's fetch is not native
 * either.
 */
export function borrowNativeFetch(doc: Document | null = typeof document !== 'undefined' ? document : null): typeof fetch | null {
  if (!doc?.body) return null;
  try {
    if (!borrowedFrame || !borrowedFrame.isConnected) {
      const frame = doc.createElement('iframe');
      frame.hidden = true;
      frame.tabIndex = -1;
      frame.setAttribute('aria-hidden', 'true');
      doc.body.appendChild(frame);
      borrowedFrame = frame;
    }
    const frameWindow = borrowedFrame.contentWindow as (Window & typeof globalThis) | null;
    const frameFetch = frameWindow?.fetch;
    if (!frameWindow || !isNativeFunction(frameFetch)) return null;
    return frameFetch.bind(frameWindow);
  } catch {
    return null;
  }
}

/** Forget the borrowed frame. For tests. */
export function resetBorrowedFetch(): void {
  borrowedFrame?.remove();
  borrowedFrame = null;
}

/** Whether a throw from `pageFetch` is the wrapper's fault, not the network's. */
function thrownByPageShim(error: unknown, pageFetch: unknown): boolean {
  if (typeof window === 'undefined') return false;
  if (error instanceof TypeError) return false;
  if (error instanceof Error && error.name === 'AbortError') return false;
  return !isNativeFunction(pageFetch);
}

/**
 * `fetch(url)`, asked once more through the browser's own fetch when a page
 * script's replacement threw. The URL is made absolute first: a blank frame
 * resolves relative URLs against its own document.
 */
export async function fetchPastPageShim(
  url: string,
  borrow: () => typeof fetch | null = borrowNativeFetch
): Promise<Response> {
  const pageFetch = globalThis.fetch;
  try {
    return await pageFetch(url);
  } catch (error) {
    if (!thrownByPageShim(error, pageFetch)) throw error;
    const nativeFetch = borrow();
    if (!nativeFetch) throw error;
    try {
      return await nativeFetch(new URL(url, window.location.href).href);
    } catch {
      // Keep the wrapper's error: a row still naming it after this shipped
      // means the clean fetch did not get past the host either.
      throw error;
    }
  }
}
